
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, Zap, Target, Loader2, X, ChevronRight, CheckCircle2, History, Compass, Brain, Lightbulb, ArrowRight, MessageCircle, Trash2 } from 'lucide-react';
import { Decision, LifeContext, JournalEntry, Screen, AIPersonalityKey } from '../types';
import { mapDecision, UserFeedbackContext } from '../services/openaiService';
import { UI_STRINGS } from '../constants/translations';
import { trackPageView, trackDecisionRecorded } from '../src/lib/analytics';
import { useFeatureDiscovery } from '../contexts/FeatureDiscoveryContext';
import FeatureDiscovery from '../components/FeatureDiscovery';
import { PersonalityHeaderButton, PersonalitySelectorModal } from '../components/PersonalitySelector';
import { DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

interface DecisionsViewProps {
  context: LifeContext;
  entries: JournalEntry[];
  decisions: Decision[];
  setDecisions: React.Dispatch<React.SetStateAction<Decision[]>>;
  onResolve: (id: string) => void;
  onDelete?: (id: string) => void;
  onNavigate?: (screen: Screen) => void;
  onDecisionCreated?: (decision: Decision) => void;
  onPersonalityChange?: (key: AIPersonalityKey) => Promise<void>;
}

const DecisionsView: React.FC<DecisionsViewProps> = ({ context, entries, decisions, setDecisions, onResolve, onDelete, onNavigate, onDecisionCreated, onPersonalityChange }) => {
  const t = useMemo(() => UI_STRINGS[context.language] || UI_STRINGS.English, [context.language]);
  const [isAdding, setIsAdding] = useState(false);
  const [newDecisionTitle, setNewDecisionTitle] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeDecisionId, setActiveDecisionId] = useState<string | null>(null);
  const [feedbackContext, setFeedbackContext] = useState<UserFeedbackContext | null>(null);
  const { state: discoveryState, getNextStepForView, completeStep } = useFeatureDiscovery();
  const currentDiscoveryStep = useMemo(() => getNextStepForView('decisions'), [getNextStepForView]);

  // AI Personality state
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const currentPersonalityKey = context.selectedPersonalityKey || DEFAULT_PERSONALITY_KEY;

  // Load feedback context on mount
  useEffect(() => {
    fetch('/api/insight-feedback/ai-context', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setFeedbackContext(data))
      .catch(err => console.error('Error loading feedback context:', err));
  }, []);

  const handleDiscoveryActivate = useCallback((step: keyof typeof discoveryState) => {
    if (step === 'decisionsDiscovered') {
      setIsAdding(true);
      setTimeout(() => {
        const input = document.querySelector('textarea[placeholder]') as HTMLTextAreaElement;
        if (input) {
          input.focus();
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    trackPageView('/decisions', 'Decisions', true);
  }, []);

  useEffect(() => {
    const prefillContext = sessionStorage.getItem('newDecisionContext');
    if (prefillContext) {
      sessionStorage.removeItem('newDecisionContext');
      try {
        const ctx = JSON.parse(prefillContext);
        if (ctx.prefill) {
          setNewDecisionTitle(ctx.prefill);
          setIsAdding(true);
        }
      } catch {}
    }
  }, []);

  const handleStartAnalysis = async () => {
    if (!newDecisionTitle.trim()) return;
    setIsAnalyzing(true);
    try {
      const result = await mapDecision(newDecisionTitle, context, entries, [], decisions, feedbackContext || undefined);
      const decision: Decision = {
        id: Math.random().toString(36).substr(2, 9),
        context: "Manuale",
        ...result,
        title: newDecisionTitle,
        status: 'pending',
        timestamp: Date.now()
      };
      setDecisions(prev => [decision, ...(Array.isArray(prev) ? prev : [])]);
      setActiveDecisionId(decision.id);
      setIsAdding(false);
      setNewDecisionTitle('');
      trackDecisionRecorded();
      onDecisionCreated?.(decision);
      if (!discoveryState.decisionsDiscovered) {
        completeStep('decisionsDiscovered');
        setTimeout(() => onNavigate?.('insights'), 500);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#F8F9FA] view-enter overflow-hidden">
      <header className="px-6 lg:px-12 xl:px-16 pt-5 lg:pt-5 pb-4 lg:pb-5 bg-white border-b border-black/[0.03] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto w-full flex justify-between items-center">
          <div>
            <h1 className="text-[30px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none">{t.navDecisions}</h1>
            <p className="text-[10px] lg:text-xs xl:text-sm font-black uppercase tracking-widest text-black/20 mt-1.5 lg:mt-2">
              {t.decisionsPage.subtitle}
            </p>
          </div>
          {/* Right side: Add button + AI Personality Button */}
          <div className="flex items-center gap-3">
            {!isAdding && (
              <button onClick={() => setIsAdding(true)} className="w-12 h-12 lg:w-14 lg:h-14 bg-indigo-600 text-white rounded-[18px] lg:rounded-[22px] flex items-center justify-center active-scale hover:bg-indigo-700 hover:shadow-xl transition-all shadow-lg group">
                <Plus size={24} className="lg:w-6 lg:h-6 group-hover:rotate-90 transition-transform duration-300" />
              </button>
            )}
            {/* AI Personality Button - Rightmost */}
            <PersonalityHeaderButton
              personalityKey={currentPersonalityKey}
              language={context.language}
              onClick={() => setIsPersonalityModalOpen(true)}
            />
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 lg:px-16 xl:px-24 py-6 lg:py-10 no-scrollbar pb-32 lg:pb-40">
        <div className="max-w-6xl mx-auto">
        {isAdding && (
          <div className="bg-white p-6 lg:p-10 xl:p-12 rounded-[36px] lg:rounded-[48px] border border-indigo-100 shadow-2xl animate-in slide-in-from-bottom-6 max-w-4xl mx-auto mb-8">
             <div className="flex justify-between items-center mb-6 lg:mb-8">
                <span className="text-[10px] lg:text-xs font-black uppercase tracking-widest text-indigo-500 flex items-center gap-2"><Brain size={14} className="lg:w-5 lg:h-5" /> {t.decisionsPage.strategicAnalysis}</span>
                <button onClick={() => setIsAdding(false)} className="p-2 lg:p-3 text-black/20 hover:text-black/40 hover:bg-black/5 rounded-xl transition-colors"><X size={24} className="lg:w-7 lg:h-7" /></button>
             </div>
             <div className="lg:flex lg:items-start lg:gap-6">
               <textarea 
                  autoFocus
                  value={newDecisionTitle}
                  onChange={(e) => setNewDecisionTitle(e.target.value)}
                  placeholder={t.decisionsPage.choicePlaceholder}
                  className="w-full bg-transparent text-[22px] lg:text-2xl xl:text-3xl font-bold tracking-tight outline-none placeholder:text-black/10 mb-6 lg:mb-0 min-h-[100px] lg:min-h-[80px] resize-none leading-tight flex-1"
               />
               <button 
                  onClick={handleStartAnalysis}
                  disabled={!newDecisionTitle.trim() || isAnalyzing}
                  className="w-full lg:w-auto lg:px-10 h-14 lg:h-16 bg-indigo-600 text-white rounded-[24px] lg:rounded-full font-bold text-[15px] lg:text-base flex items-center justify-center gap-2 active-scale disabled:opacity-20 shadow-xl hover:bg-indigo-700 hover:shadow-2xl transition-all lg:flex-shrink-0"
                >
                  {isAnalyzing ? <Loader2 className="animate-spin" size={20} /> : <><Zap size={18} className="lg:w-5 lg:h-5" /> {t.decisionsPage.analyzeDilemma}</>}
               </button>
             </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">

        {Array.isArray(decisions) && decisions.map((d: Decision) => (
          <div key={d.id} onClick={() => setActiveDecisionId(activeDecisionId === d.id ? null : d.id)} className={`p-6 lg:p-8 xl:p-10 rounded-[32px] lg:rounded-[40px] border transition-all duration-300 cursor-pointer hover:shadow-lg ${activeDecisionId === d.id ? 'bg-white border-indigo-200 shadow-2xl col-span-full' : 'bg-white border-black/[0.03] shadow-sm hover:border-indigo-100 hover:shadow-md'}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                 <h3 className={`text-[19px] lg:text-xl xl:text-2xl font-bold tracking-tight leading-snug ${d.status === 'resolved' ? 'line-through opacity-30 text-black/50' : 'text-black'}`}>{d.title || d.analysisSummary || t.decisionsPage.inAnalysis}</h3>
                 <div className="flex items-center gap-2 mt-2 lg:mt-3">
                    <span className={`px-3 py-1 rounded-full text-[9px] lg:text-[10px] font-black uppercase tracking-widest ${d.status === 'pending' ? 'bg-indigo-50 text-indigo-500' : 'bg-emerald-50 text-emerald-500'}`}>
                      {d.status === 'pending' ? (d.bestOption ? t.decisionsPage.analyzed : t.decisionsPage.inAnalysis) : t.decisionsPage.decided}
                    </span>
                 </div>
              </div>
              <ChevronRight size={20} className={`transition-transform duration-300 mt-1 lg:w-6 lg:h-6 ${activeDecisionId === d.id ? 'rotate-90 text-indigo-500' : 'opacity-10'}`} />
            </div>

            {activeDecisionId === d.id && (
              <div className="mt-6 lg:mt-8 animate-in fade-in">
                <div className="lg:grid lg:grid-cols-2 lg:gap-8 xl:gap-10 space-y-6 lg:space-y-0">
                  <div className="space-y-6">
                    {d.bestOption && (
                      <div className="p-6 lg:p-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-[24px] lg:rounded-[28px] text-white shadow-xl">
                        <div className="flex items-center gap-2 mb-3 opacity-80">
                          <Lightbulb size={18} className="lg:w-5 lg:h-5" />
                          <span className="text-[10px] lg:text-xs font-black uppercase tracking-widest text-white/70">{t.decisionsPage.aiAdvice}</span>
                        </div>
                        <p className="text-[18px] lg:text-xl xl:text-2xl font-black mb-2">{t.decisionsPage.suggested}: {d.bestOption}</p>
                        <p className="text-[14px] lg:text-base opacity-85 leading-relaxed font-medium">{d.reasonWhy}</p>
                      </div>
                    )}

                    {d.nextAction && (
                      <div className="space-y-3">
                        <div className="p-5 lg:p-6 bg-emerald-50 rounded-[20px] lg:rounded-[24px] border border-emerald-100 flex items-center justify-between">
                          <div className="flex-1">
                            <span className="text-[9px] lg:text-[10px] font-black uppercase tracking-widest text-emerald-600">{t.decisionsPage.nextStep}</span>
                            <p className="text-[14px] lg:text-base font-bold text-emerald-900 leading-tight mt-1">{d.nextAction}</p>
                          </div>
                          <ArrowRight size={22} className="text-emerald-500 shrink-0 ml-4 lg:w-6 lg:h-6" />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            sessionStorage.setItem('chatContext', JSON.stringify({
                              type: 'decision_next_step',
                              data: {
                                decisionId: d.id,
                                decisionTitle: d.title,
                                bestOption: d.bestOption,
                                reasonWhy: d.reasonWhy,
                                nextAction: d.nextAction,
                                options: d.options?.map(opt => ({
                                  title: opt.title,
                                  alignmentScore: opt.alignmentScore,
                                  pros: opt.pros,
                                  cons: opt.cons
                                }))
                              }
                            }));
                            onNavigate?.('consult');
                          }}
                          className="w-full py-3.5 lg:py-4 rounded-xl lg:rounded-2xl text-[11px] lg:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 active-scale bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                        >
                          <MessageCircle size={14} className="lg:w-4 lg:h-4" /> {t.decisionsPage.discussNextStep}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <span className="text-[10px] lg:text-xs font-black uppercase tracking-widest opacity-30 px-1 block">{t.decisionsPage.compareOptions}</span>
                    <div className="space-y-4 lg:space-y-5">
                      {d.options?.map((opt: any) => (
                        <div key={opt.id} className="p-5 lg:p-6 bg-gray-50 rounded-[24px] lg:rounded-[28px] border border-black/[0.03] hover:border-indigo-100 transition-colors">
                          <div className="flex justify-between items-center mb-4">
                            <span className="font-bold text-black lg:text-lg">{opt.title}</span>
                            <div className="flex items-center gap-1.5 bg-indigo-50 px-3 py-1.5 rounded-full">
                              <Target size={12} className="text-indigo-500" />
                              <span className="text-[11px] lg:text-xs font-black text-indigo-600">{opt.alignmentScore}%</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 lg:gap-6">
                            <div className="space-y-2">
                              <span className="text-[9px] lg:text-[10px] font-black text-emerald-500 uppercase">{t.decisions.pros}</span>
                              {opt.pros?.slice(0, 3).map((p: string, i: number) => <p key={i} className="text-[11px] lg:text-sm text-black/60 leading-snug">• {p}</p>)}
                            </div>
                            <div className="space-y-2">
                              <span className="text-[9px] lg:text-[10px] font-black text-rose-400 uppercase">{t.decisions.cons}</span>
                              {opt.cons?.slice(0, 3).map((c: string, i: number) => <p key={i} className="text-[11px] lg:text-sm text-black/60 leading-snug">• {c}</p>)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 mt-8 lg:mt-10 pt-6 lg:pt-8 border-t border-black/5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      sessionStorage.setItem('chatContext', JSON.stringify({
                        type: 'decision_analysis',
                        data: { decisionId: d.id, decisionTitle: d.title }
                      }));
                      onNavigate?.('consult');
                    }}
                    className="flex-1 py-4 lg:py-5 rounded-2xl text-[12px] lg:text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2 active-scale shadow-sm bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-100 transition-colors"
                  >
                    <MessageCircle size={16} className="lg:w-5 lg:h-5" /> {t.decisionsPage.discussInChat}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onResolve(d.id); }} className={`flex-1 py-4 lg:py-5 rounded-2xl text-[12px] lg:text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2 active-scale shadow-sm transition-colors ${d.status === 'pending' ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-200 text-black/40 hover:bg-gray-300'}`}>
                    {d.status === 'pending' ? <><CheckCircle2 size={16} className="lg:w-5 lg:h-5" /> {t.decisionsPage.decided}</> : <><History size={16} className="lg:w-5 lg:h-5" /> {t.decisionsPage.reopen}</>}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(t.journal.deleteConfirm)) {
                        onDelete?.(d.id);
                      }
                    }}
                    className="py-4 lg:py-5 px-6 rounded-2xl text-[12px] lg:text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 text-rose-400 hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 size={16} className="lg:w-5 lg:h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {(!decisions || decisions.length === 0) && !isAdding && (
          <div className="col-span-full flex flex-col items-center justify-center py-20 lg:py-32 text-center">
            <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center mb-6 lg:mb-8 shadow-lg">
              <Compass size={40} className="text-indigo-400 lg:w-14 lg:h-14" />
            </div>
            <p className="font-bold text-black/30 mb-8 lg:text-xl xl:text-2xl max-w-md">{t.decisionsPage.waitingForChoice}</p>
            <button
              onClick={() => {
                sessionStorage.setItem('chatContext', JSON.stringify({
                  type: 'decision_analysis',
                  data: { decisionTitle: 'a decision to make' }
                }));
                onNavigate?.('consult');
              }}
              className="px-8 lg:px-12 py-4 lg:py-5 bg-indigo-600 text-white rounded-2xl lg:rounded-full font-bold text-[14px] lg:text-lg flex items-center gap-3 active-scale hover:bg-indigo-700 hover:shadow-2xl transition-all shadow-xl"
            >
              <MessageCircle size={20} className="lg:w-6 lg:h-6" /> {t.decisionsPage.talkInChat}
            </button>
          </div>
        )}
        </div>
        </div>
      </div>

      {currentDiscoveryStep && (
        <FeatureDiscovery
          featureKey={currentDiscoveryStep}
          discoveryState={discoveryState}
          onDismiss={completeStep}
          onActivate={handleDiscoveryActivate}
          onNavigate={onNavigate}
          currentView="decisions"
          language={context.language}
          position="bottom"
        />
      )}

      {/* AI Personality Selector Modal */}
      <PersonalitySelectorModal
        isOpen={isPersonalityModalOpen}
        onClose={() => setIsPersonalityModalOpen(false)}
        currentPersonalityKey={currentPersonalityKey}
        onSelect={onPersonalityChange!}
        language={context.language}
      />
    </div>
  );
};

export default DecisionsView;
